import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, unref as _unref, withCtx as _withCtx, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, createElementBlock as _createElementBlock, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "ml-auto" }
const _hoisted_2 = ["textContent"]

import { computed, onMounted, ref, Ref } from 'vue';
import Chip from 'primevue/chip';
import Column from 'primevue/column';
import DataTable from 'primevue/datatable';
import { AxiosResponse } from 'axios';
import ArchiveSwitch from '@/components/ArchiveSwitch';
import AddOrganizationModal from '@/components/AddOrganizationModal';
import { PERMISSIONS } from '@/enum/permissions';
import { can } from '@/composables';
import { useOrganizationArchiveStore, useOrganizationStore } from '@/store';
import { OrganizationsInterface, SubOrganizationTableType } from '@/types';


export default /*@__PURE__*/_defineComponent({
  __name: 'OrganizationsConnectionsSubOrganizationTable',
  setup(__props) {

const organizationStore = useOrganizationStore();
const archiveStore = useOrganizationArchiveStore();
const subOrganizations: Ref<SubOrganizationTableType[]> = ref([]);
const subOrganizationCount: Ref<number> = computed(() => (subOrganizations.value ? subOrganizations.value.length : 0));
const attachOrganizationModalOpen: Ref<boolean> = ref(false);
const organizationModalItems: Ref<OrganizationsInterface[]> = ref([]);

onMounted((): void => {
  if (can([PERMISSIONS.ORGANIZATION_SUB_ORGANIZATIONS])) getSubOrganizations();
});

const getSubOrganizations: (forceRequest?: boolean) => Promise<void> = async (forceRequest = false) => {
  const filterArchived = organizationStore.showArchived ? { archived: '1' } : undefined;
  await organizationStore
    .getSubOrganizationsForTable(forceRequest, filterArchived)
    .then((result) => (subOrganizations.value = result))
    .catch(() => (subOrganizations.value = []));
};

const createMenuModel = (item: SubOrganizationTableType) => {
  const menuItems = [
    {
      label: 'Archive',
      visible: can([PERMISSIONS.ORGANIZATION_ARCHIVE]),
      command: async () => {
        await archiveStore.archive(item.id as number).catch(() => {
          archiveStore.doToast(`Archiving failed`, 'Archived', true);
          return;
        });
        getSubOrganizations(true);
        archiveStore.doToast(`${item.name} has been archived`, 'Archived');
      },
    },
  ];

  if (item.archived_at) {
    menuItems[0] = {
      label: 'Unarchive',
      visible: can([PERMISSIONS.ORGANIZATION_ARCHIVE]),
      command: async () => {
        await archiveStore.unArchive(item.id as number).catch(() => {
          archiveStore.doToast(`Unarchiving failed`, 'Unarchived', true);
          return;
        });
        getSubOrganizations(true);
        archiveStore.doToast(`${item.name} has been restored`, 'Unarchived');
      },
    };
  }
  return menuItems;
};

const attachSubOrganization: (ids: number[]) => Promise<void> = async (ids) => {
  const requests: Promise<void | AxiosResponse>[] = [];
  let failedRequest = 0;
  let detail = 'Organization(s) has been added';

  ids.forEach(async (id) => {
    const payload = organizationStore.organizations.data.find(
      (organization) => organization.key === id,
    ) as OrganizationsInterface;
    payload.data.parent_id = organizationStore.organization.id;
    requests.push(
      organizationStore.updateOrganization(payload.data, id).catch(() => {
        organizationStore.doToast('Adding Sub-organization failed', 'Add Sub-organization', true);

        failedRequest++;
        return;
      }),
    );
  });

  await Promise.all(requests);

  if (failedRequest) {
    if (ids.length > 1 && failedRequest !== ids.length) {
      detail = 'Some organzations were added, some failed to be added.';
    } else {
      return;
    }
  }

  getSubOrganizations(true);
  organizationStore.doToast(detail, 'Added');
};

const openAddOrganizations = async () => {
  organizationModalItems.value = await organizationStore.getAddOrganizationsModalData();
  attachOrganizationModalOpen.value = true;
};

const onCancelAdd = () => (attachOrganizationModalOpen.value = false);

const onSaveAdd = (subOrganizations: number[]) => {
  attachSubOrganization(subOrganizations);
  attachOrganizationModalOpen.value = false;
};
const showArchived = () => {
  organizationStore.showArchived = true;
  getSubOrganizations();
};
const hideArchived = () => {
  organizationStore.showArchived = false;
  getSubOrganizations();
};

return (_ctx: any,_cache: any) => {
  const _component_AppButton = _resolveComponent("AppButton")!
  const _component_RouterLink = _resolveComponent("RouterLink")!
  const _component_ActionMenu = _resolveComponent("ActionMenu")!
  const _component_Card = _resolveComponent("Card")!

  return (_openBlock(), _createBlock(_component_Card, null, {
    header: _withCtx(() => [
      _cache[0] || (_cache[0] = _createElementVNode("h2", null, "Sub-organizations", -1)),
      _createVNode(_unref(Chip), null, {
        default: _withCtx(() => [
          _createTextVNode(_toDisplayString(subOrganizationCount.value), 1)
        ]),
        _: 1
      }),
      (_unref(can)([_unref(PERMISSIONS).ORGANIZATION_SHOW_ARCHIVE]))
        ? (_openBlock(), _createBlock(_unref(ArchiveSwitch), {
            key: 0,
            onEnable: showArchived,
            onDisable: hideArchived
          }))
        : _createCommentVNode("", true),
      _createElementVNode("div", _hoisted_1, [
        (_unref(can)([_unref(PERMISSIONS).ORGANIZATION_UPDATE]))
          ? (_openBlock(), _createBlock(_component_AppButton, {
              key: 0,
              type: "text",
              size: "small",
              text: "Add Sub-organizations",
              "icon-left": "add",
              onClick: openAddOrganizations
            }))
          : _createCommentVNode("", true)
      ])
    ]),
    content: _withCtx(() => [
      _createVNode(_unref(DataTable), {
        value: subOrganizations.value,
        "global-filter-fields": ['name'],
        scrollable: true,
        "loading-icon": "logo-spinner",
        "data-key": "name",
        "responsive-layout": "scroll",
        "scroll-height": "440px",
        "state-key": "session-sub-organization-store-table",
        "state-storage": "session"
      }, {
        empty: _withCtx(() => [...(_cache[1] || (_cache[1] = [
          _createTextVNode("Organizations not assigned", -1)
        ]))]),
        default: _withCtx(() => [
          (subOrganizationCount.value)
            ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                _createVNode(_unref(Column), {
                  "body-class": "mw-20rem",
                  "header-class": "mw-20rem",
                  field: "name",
                  header: "Organization",
                  sortable: ""
                }, {
                  body: _withCtx(({ data }) => [
                    (_unref(can)([_unref(PERMISSIONS).ORGANIZATIONS_CONNECTIONS]))
                      ? (_openBlock(), _createBlock(_component_RouterLink, {
                          key: 0,
                          to: `/organizations/${data.id}`,
                          class: "font-semibold",
                          textContent: _toDisplayString(data.name)
                        }, null, 8, ["to", "textContent"]))
                      : (_openBlock(), _createElementBlock("span", {
                          key: 1,
                          class: "font-semibold",
                          textContent: _toDisplayString(data.id)
                        }, null, 8, _hoisted_2))
                  ]),
                  _: 1
                }),
                _createVNode(_unref(Column), {
                  field: "",
                  header: ""
                }),
                _createVNode(_unref(Column), {
                  "body-class": "justify-content-center mw-10rem",
                  "header-class": "justify-content-center mw-10rem",
                  field: "id",
                  header: "Actions"
                }, {
                  body: _withCtx(({ data }) => [
                    _createVNode(_component_ActionMenu, {
                      "menu-items": createMenuModel(data),
                      icon: "more_horiz"
                    }, null, 8, ["menu-items"])
                  ]),
                  _: 1
                })
              ], 64))
            : _createCommentVNode("", true)
        ]),
        _: 1
      }, 8, ["value"]),
      (attachOrganizationModalOpen.value && _unref(can)([_unref(PERMISSIONS).ORGANIZATION_UPDATE]))
        ? (_openBlock(), _createBlock(_unref(AddOrganizationModal), {
            key: 0,
            title: "Add Sub-organizations",
            items: organizationModalItems.value,
            searchable: true,
            "multi-selection": true,
            onSave: onSaveAdd,
            onCancel: onCancelAdd
          }, null, 8, ["items"]))
        : _createCommentVNode("", true)
    ]),
    _: 1
  }))
}
}

})